import { useDispatch, useSelector } from "react-redux";
import { CustomizationProvider } from "@twilio-paste/core/customization";
import { useEffect } from "react";

import { RootContainer } from "./RootContainer";
import { AppState, EngagementPhase } from "../store/definitions";
import { sessionDataHandler } from "../sessionDataHandler";
import { initSession } from "../store/actions/initActions";
import { changeEngagementPhase, updatePendingSession } from "../store/actions/genericActions";
import type { AppDispatch } from "../store/store";

export function WebchatWidget() {
    const theme = useSelector((state: AppState) => state.config.theme);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        const resumeSession = async () => {
            const data = sessionDataHandler.tryResumeExistingSession();
            if (data) {
                // The stored session is kept for the next submit, which reuses it instead of creating a new conversation.
                // Its query is not stored, so the submitted text is sent as a new message.
                const keepForRetry = () => dispatch(updatePendingSession({ session: data, query: "" }));
                try {
                    const conversation = await dispatch(
                        initSession({ token: data.token, conversationSid: data.conversationSid })
                    );
                    if (!conversation) {
                        keepForRetry();
                    }
                } catch (e) {
                    // if initSession fails, go to changeEngagement phase - most likely there's something wrong with the store token or conversation sis
                    keepForRetry();
                    dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                }
            } else {
                // if no token is stored, got engagement form
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            }
        };
        resumeSession();
    }, [dispatch]);

    return (
        <div style={{ minHeight: "100%", minWidth: "100%" }}>
            <CustomizationProvider
                baseTheme={theme?.isLight ? "default" : "dark"}
                theme={theme?.overrides}
                elements={{
                    MESSAGE_INPUT: {
                        boxShadow: "none!important" as "none"
                    },
                    MESSAGE_INPUT_BOX: {
                        display: "inline-block",
                        boxShadow: "none"
                    },
                    ALERT: {
                        paddingTop: "space30",
                        paddingBottom: "space30"
                    },
                    BUTTON: {
                        "&[aria-disabled='true'][color='colorTextLink']": {
                            color: "colorTextLinkWeak"
                        }
                    }
                }}
            >
                <RootContainer />
            </CustomizationProvider>
        </div>
    );
}
